import { resolveOtherInfoObj } from '@moko/utils';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  PagedDataGrid,
  type IPagedDataGridRefType,
  type DataGridColumn,
  type ICommandBarItem,
} from '@moko/ui';
import { FontIcon, Link } from '@fluentui/react';
import { People24Regular } from '@fluentui/react-icons';
import { getMetadata, IField } from '../../services/metadataService';
import {
  exportContractors,
  getContractors,
  importContractors,
  IContractor,
  type IImportContractorsResponse,
} from '../../services/contractorService';
import { getWorkStatuses, IWorkStatus } from '../../services/workService';
import { useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  clearMessageAtom,
  errorMessageAtom,
  isInProgressAtom,
} from '../../atoms/messageBarAtoms';
import { appRoles } from '../../authConfig';
import AppContext from '../AppContext';
import { useTranslation } from 'react-i18next';
import WorkStatusBar from './WorkStatusBar';
import ImportPanel from '../../common/ImportPanel';
import ImportRowResults from '../../common/ImportRowResults';
import { useExportCsv } from '../../common/useExportCsv';

type IContractorItem = IContractor & {
  [key: string]: any;
};

// Sum a contractor's per-status work counts (API returns them as stringy values).
const sumWorks = (
  works: Record<string, string | number> | undefined | null
): number => {
  if (!works) return 0;
  return Object.values(works).reduce<number>(
    (sum, v) =>
      sum + (typeof v === 'number' ? v : Number.parseInt(String(v), 10) || 0),
    0
  );
};

const ContractorsV2 = () => {
  const { t } = useTranslation();

  const profileData = useAtomValue(profileDataAtom);
  const navigate = useNavigate();

  const [selectedContractorId, setSelectedContractorId] = useState<number>();
  const pagedDataGridRef = useRef<IPagedDataGridRefType>(null);
  const isAdmin = profileData.roles.includes(appRoles.Admin);
  const [importPanelOpen, setImportPanelOpen] = useState(false);
  const handleExport = useExportCsv(exportContractors, 'contractors');

  const context = useContext(AppContext);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const clearMessage = useSetAtom(clearMessageAtom);

  const [workStatuses, setWorkStatuses] = useState<IWorkStatus[]>([]);
  const [otherInfoColumns, setOtherInfoColumns] = useState<
    DataGridColumn<IContractorItem>[]
  >([]);
  const [otherInfoFieldNames, setOtherInfoFieldNames] = useState<string[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const abortController = new AbortController();
      setIsInProgress(true);

      try {
        const woStatuses: IWorkStatus[] = await getWorkStatuses(
          abortController
        );
        setWorkStatuses(woStatuses);

        const metadata = await getMetadata(abortController, 'Contractor');
        const otherInfoCols = metadata?.fields
          .filter((mf: IField) => mf.showInGrid)
          .map((oif: IField) => ({
            key: oif.name,
            name: oif.name,
            width: 100,
            type:
              oif.fieldType === 'Date'
                ? ('date' as const)
                : oif.fieldType === 'DateTime'
                ? ('datetime' as const)
                : undefined,
            ...(oif.fieldType === 'Image' && {
              renderCell: (item: IContractorItem) => (
                <img src={item[oif.name]} style={{ width: 20 }} alt="" />
              ),
            }),
          }));
        setOtherInfoColumns(otherInfoCols || []);
        const fieldNames: string[] = (otherInfoCols || []).map(
          (oic: DataGridColumn<IContractorItem>) => oic.name ?? ''
        );
        setOtherInfoFieldNames(fieldNames);

        clearMessage();
        context.setSelectedTab(t('contractors'));
      } catch (error: any) {
        setErrorMessage(error.message);
      } finally {
        setIsInProgress(false);
      }
      return () => {
        abortController.abort();
      };
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusByCode = useMemo(() => {
    const map = new Map<string, { name: string; color?: string }>();
    for (const wos of workStatuses ?? []) {
      map.set(wos.code, { name: wos.name ?? wos.code, color: wos.color });
    }
    return map;
  }, [workStatuses]);

  const baseColumns: DataGridColumn<IContractorItem>[] = [
    {
      key: 'id',
      name: 'Id',
      width: 50,
      isSortable: true,
      renderCell: (item: IContractorItem) => (
        <Link onClick={() => navigate(`/editContractor?id=${item.id}`)}>
          {item.id}
        </Link>
      ),
    },
    {
      key: 'name',
      name: 'Name',
      width: 250,
      isSortable: true,
    },
    {
      key: 'code',
      name: 'Code',
      width: 100,
      isSortable: true,
    },
    {
      key: 'works',
      name: 'Works',
      width: 180,
      renderCell: (item) =>
        item.works ? (
          <WorkStatusBar
            works={item.works}
            statusByCode={statusByCode}
            total={item.__worksTotal ?? sumWorks(item.works)}
          />
        ) : (
          <span>No works</span>
        ),
    },
    {
      key: 'createdAt',
      name: 'Created at',
      width: 155,
      type: 'datetime',
    },
  ];

  const columns = useMemo(
    () => [...baseColumns, ...otherInfoColumns],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workStatuses, otherInfoColumns]
  );

  const commandBarItems: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: 'Refresh',
        icon: <FontIcon iconName="Refresh" />,
        onClick: (ctx) => ctx.refresh(),
      },
      {
        key: 'newContractor',
        text: 'New',
        icon: <FontIcon iconName="Add" />,
        onClick: () => navigate('/newContractor'),
        disabled: !isAdmin,
      },
      {
        key: 'editContractor',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        onClick: () => navigate(`/editContractor?id=${selectedContractorId}`),
        disabled: !isAdmin || !selectedContractorId,
      },
      {
        key: 'importContractors',
        text: 'Import',
        icon: <FontIcon iconName="CloudUpload" />,
        onClick: () => setImportPanelOpen(true),
        disabled: !isAdmin,
      },
      {
        key: 'exportContractors',
        text: 'Export',
        icon: <FontIcon iconName="CloudDownload" />,
        onClick: () => {
          void handleExport();
        },
        disabled: !isAdmin,
      },
    ],
    [isAdmin, selectedContractorId, navigate, handleExport]
  );

  return (
    <div style={{ maxWidth: 1000 }}>
      <PagedDataGrid<IContractorItem>
        ref={pagedDataGridRef}
        columns={columns}
        getAction={async (
          abortController,
          pageSize,
          pageNo,
          orderBy,
          search
        ) => {
          const data = await getContractors(
            abortController,
            pageSize,
            pageNo,
            orderBy,
            search
          );
          const items = data.items.map((contractor) => {
            const newContractor: any = { ...contractor };
            const oi = resolveOtherInfoObj(contractor.otherInfo);
            if (oi) {
              otherInfoFieldNames.forEach((fieldName) => {
                const val = oi[fieldName];
                newContractor[fieldName] = val;
                if (
                  typeof val === 'string' &&
                  val.includes('.blob.core.windows.net/attachments/')
                ) {
                  newContractor[fieldName] = `${val.replace(
                    'attachments',
                    'thumbnails'
                  )}.png`;
                }
              });
            }
            return newContractor;
          }) as IContractorItem[];

          // Each contractor's bar is full-width and split by status percentage,
          // so we only need the per-row total. Precompute it once for the label.
          items.forEach((c) => {
            c.__worksTotal = sumWorks(c.works);
          });

          return { ...data, items };
        }}
        commandBarItems={commandBarItems}
        selectionMode="single"
        onSelectionChange={(selection) => {
          setSelectedContractorId(Number(selection.id));
        }}
        onRowDoubleClicked={(selection) => {
          navigate(`/editContractor?id=${selection.id}`);
        }}
        listHeigh={525}
        loadingConfig={{
          message: t('loadingContractors') || 'Loading contractors...',
          icon: (
            <People24Regular
              style={{ width: 125, height: 125, color: 'lightgray' }}
            />
          ),
        }}
      />
      <ImportPanel<IImportContractorsResponse>
        isOpen={importPanelOpen}
        headerText="Import Contractors"
        onDismiss={() => setImportPanelOpen(false)}
        importApi={importContractors}
        getErrorsCount={(r) => r.errorsCount}
        onImportSucceeded={(r) =>
          `Import completed. Added: ${r.createdCount}, Updated: ${r.updatedCount}, Error: ${r.errorsCount}.`
        }
        onAfterImport={() => pagedDataGridRef.current?.refresh()}
        renderResults={(r) => (
          <ImportRowResults
            rows={r.importContractorResponse}
            createdCount={r.createdCount}
            updatedCount={r.updatedCount}
            errorsCount={r.errorsCount}
            messages={r.messages}
          />
        )}
      />
    </div>
  );
};

export default ContractorsV2;
